import {
  Box,
  Flex,
  IconButton,
  Text,
  useBreakpointValue,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { ReactNode, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCustomTheme } from "../../contexts/CustomThemeContext";

export interface CarouselProduct {
  id: string;
  title: string;
  image?: string;
  description?: string;
  navigateTo?: string;
  leftIcon?: ReactNode;
  leftIconBg?: string;
  leftIconColor?: string;
  meta?: string;
}

interface ProductCarouselProps {
  products: CarouselProduct[];
  slidesToShow?: {
    base?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
  };
  itemRoutePrefix?: string;
}

const cardsBorderRadii ="8px";

const ProductCarouselTwoColumn: React.FC<ProductCarouselProps> = ({
  products,
  slidesToShow = { base: 2, sm: 3, md: 5, lg: 6, xl: 7 },
  itemRoutePrefix = "/product",
}) => {
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);
  const slideCount = useBreakpointValue(slidesToShow);
  const slideWidth = slideCount ? `${100 / slideCount}%` : "100%";
  const [hasAutoScrolled, setHasAutoScrolled] = useState(false);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const { theme } = useCustomTheme();
  const isDark = theme.name === "dark";

  const cardBg = theme.surface;
  const softBg = theme.surfaceAlt;
  const pageText = theme.text;
  const borderColor = theme.border;
  const borderStrong = theme.borderStrong;
  const mutedText = theme.textMuted;
  const subtleText = theme.textSubtle;

  const cardShadow = theme.shadowSm;
  const panelShadow = theme.shadowMd;

  const checkScroll = () => {
    const container = scrollRef.current;
    if (container) {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft + clientWidth < scrollWidth - 1);
    }
  };

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    checkScroll();
    container.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);

    return () => {
      container.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, []);

  useEffect(() => {
    setHasAutoScrolled(false);
  }, [products.length]);

  useEffect(() => {
    if (hasAutoScrolled || !scrollRef.current || !products.length) return;

    const container = scrollRef.current;
    const visibleColumns = slideCount || 1;

    if (products.length <= visibleColumns * 2) {
      checkScroll();
      return;
    }

    const scrollAmount = container.offsetWidth;
    const totalScrolls = Math.ceil(products.length / visibleColumns / 2);
    let currentStep = 0;

    const interval = setInterval(() => {
      if (currentStep < totalScrolls) {
        container.scrollBy({ left: scrollAmount, behavior: "smooth" });
        currentStep += 1;
      } else {
        container.scrollTo({ left: 0, behavior: "smooth" });
        clearInterval(interval);
        setHasAutoScrolled(true);
        checkScroll();
      }
    }, 2500);

    return () => clearInterval(interval);
  }, [hasAutoScrolled, products.length, slideCount]);

  const scroll = (direction: "left" | "right") => {
    if (scrollRef.current) {
      const container = scrollRef.current;
      const scrollAmount = container.offsetWidth;

      container.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });

      setTimeout(checkScroll, 300);
    }
  };

  const columns: CarouselProduct[][] = [];
  for (let i = 0; i < products.length; i += 2) {
    columns.push(products.slice(i, i + 2));
  }

  return (
    <Box
      position="relative"
      borderRadius={cardsBorderRadii}
      bg={cardBg}
      border={`1px solid ${borderColor}`}
      boxShadow={cardShadow}
      p={{ base: 2, md: 3 }}
    >
      {showLeftArrow && (
        <Box
          position="absolute"
          left={2}
          top={2}
          bottom={2}
          width={{ base: "34px", md: "68px" }}
          bgGradient={
            isDark
              ? "linear(to-r, rgba(10,12,16,0.95), transparent)"
              : "linear(to-r, rgba(255,255,255,0.95), transparent)"
          }
          zIndex={2}
          display="flex"
          alignItems="center"
          justifyContent="flex-start"
          borderRadius={cardsBorderRadii}
          pointerEvents="none"
        />
      )}

      {showRightArrow && (
        <Box
          position="absolute"
          right={2}
          top={2}
          bottom={2}
          width={{ base: "34px", md: "68px" }}
          bgGradient={
            isDark
              ? "linear(to-l, rgba(10,12,16,0.95), transparent)"
              : "linear(to-l, rgba(255,255,255,0.95), transparent)"
          }
          zIndex={2}
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          borderRadius={cardsBorderRadii}
          pointerEvents="none"
        />
      )}

      {showLeftArrow && (
        <IconButton
          aria-label="Scroll Left"
          icon={<ChevronLeftIcon boxSize={6} />}
          onClick={() => scroll("left")}
          position="absolute"
          left={1}
          top="50%"
          transform="translateY(-50%)"
          zIndex={3}
          variant="ghost"
          size={"sm"}
          bg={isDark ? "rgba(255,255,255,0.08)" : "rgba(24, 28, 42, 0.94)"}
          color="white"
          borderRadius={cardsBorderRadii}
          border={`1px solid ${borderStrong}`}
          backdropFilter="blur(8px)"
          boxShadow={panelShadow}
          _hover={{
            transform: "translateY(-50%) scale(1.03)",
            bg: isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.78)",
          }}
          _active={{ transform: "translateY(-50%) scale(0.98)" }}
          transition="all 0.2s ease"
        />
      )}

      {showRightArrow && (
        <IconButton
          aria-label="Scroll Right"
          icon={<ChevronRightIcon boxSize={6} />}
          onClick={() => scroll("right")}
          position="absolute"
          right={1}
          top="50%"
          transform="translateY(-50%)"
          zIndex={3}
          variant="ghost"
          size={"sm"}
          bg={isDark ? "rgba(255,255,255,0.08)" : "rgba(24, 28, 42, 0.94)"}
          color="white"
          borderRadius={cardsBorderRadii}
          border={`1px solid ${borderStrong}`}
          backdropFilter="blur(8px)"
          boxShadow={panelShadow}
          _hover={{
            transform: "translateY(-50%) scale(1.03)",
            bg: isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.78)",
          }}
          _active={{ transform: "translateY(-50%) scale(0.98)" }}
          transition="all 0.2s ease"
        />
      )}

      <Flex
        ref={scrollRef}
        overflowX="auto"
        scrollSnapType="x mandatory"
        sx={{
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
        }}
        position="relative"
        gap={{ base: 2, md: 3 }}
      >
        {columns.map((pair, index) => (
          <Flex
            key={index}
            flex={`0 0 ${slideWidth}`}
            direction="column"
            gap={3}
            px={1}
            scrollSnapAlign="start"
          >
            {pair.map((product) => (
              <Box
                key={product.id}
                as={Flex}
                minH={{ base: "82px", md: "90px" }}
                borderRadius={cardsBorderRadii}
                position="relative"
                border={`1px solid ${borderColor}`}
                overflow="hidden"
                alignItems="center"
                bg={softBg}
                px={{ base: 3, md: 4 }}
                py={3}
                cursor="pointer"
                boxShadow={cardShadow}
                _hover={{
                  bg: cardBg,
                  borderColor: borderStrong,
                  transform: "translateY(-1px)",
                  boxShadow: panelShadow,
                }}
                transition="all 0.2s ease"
                onClick={() =>
                  navigate(
                    product.navigateTo || `${itemRoutePrefix}/${product.id}`,
                  )
                }
              >
                <Box
                  position="absolute"
                  left={0}
                  top={0}
                  bottom={0}
                  w="4px"
                  bgGradient={
                    theme.gradients?.primaryButton ||
                    "linear(to-b, teal.400, blue.500)"
                  }
                />

                <Flex align="center" gap={3} width="100%" pl={2}>
                  {product.leftIcon && (
                    <Flex
                      flexShrink={0}
                      w={{ base: "34px", md: "38px" }}
                      h={{ base: "34px", md: "38px" }}
                      borderRadius={cardsBorderRadii}
                      align="center"
                      justify="center"
                      bg={
                        product.leftIconBg ||
                        (isDark ? "whiteAlpha.100" : "blackAlpha.50")
                      }
                      color={product.leftIconColor || pageText}
                      border={`1px solid ${borderColor}`}
                    >
                      {product.leftIcon}
                    </Flex>
                  )}

                  <Box minW={0} flex="1">
                    <Text
                      color={pageText}
                      textAlign="left"
                      width="100%"
                      fontSize={{ base: "0.8rem", md: "0.92rem" }}
                      fontWeight="700"
                      noOfLines={1}
                    >
                      {product.title}
                    </Text>

                    {!!product.description && (
                      <Text
                        mt={1}
                        color={mutedText}
                        fontSize="xs"
                        noOfLines={1}
                      >
                        {product.description}
                      </Text>
                    )}

                    {product.meta && (
                      <Text
                        mt={1}
                        color={subtleText}
                        fontSize="11px"
                        noOfLines={1}
                      >
                        {product.meta}
                      </Text>
                    )}
                  </Box>
                </Flex>
              </Box>
            ))}
          </Flex>
        ))}
      </Flex>
    </Box>
  );
};

export default ProductCarouselTwoColumn;
